import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BookOpen, Plus, Trash2, Pencil, Search, Loader2, Tag } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ['Rede', 'Servidor', 'Banco de Dados', 'Aplicação', 'Segurança', 'Cloud', 'Backup', 'Firewall', 'Outro'];

export function KBSolutionsPage() {
  const [solutions, setSolutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', category: 'Outro', tags: '', related_triggers: '' });
  const { getAuthHeader } = useAuthStore();

  const fetchData = useCallback(async () => {
    try {
      let url = `${API}/kb/solutions`;
      if (search) url += `?search=${encodeURIComponent(search)}`;
      const res = await axios.get(url, { headers: getAuthHeader() });
      setSolutions(res.data.solutions || []);
    } catch (err) { toast.error('Erro ao carregar'); }
    finally { setLoading(false); }
  }, [getAuthHeader, search]);

  useEffect(() => { const t = setTimeout(fetchData, 300); return () => clearTimeout(t); }, [fetchData]);

  const handleSave = async () => {
    if (!form.title || !form.content) { toast.error('Título e conteúdo são obrigatórios'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        related_triggers: form.related_triggers ? form.related_triggers.split(',').map(t => t.trim()).filter(Boolean) : [],
      };
      if (editItem) {
        await axios.put(`${API}/kb/solutions/${editItem.id}`, payload, { headers: getAuthHeader() });
        toast.success('Solução atualizada');
      } else {
        await axios.post(`${API}/kb/solutions`, payload, { headers: getAuthHeader() });
        toast.success('Solução criada');
      }
      setShowForm(false); setEditItem(null);
      setForm({ title: '', content: '', category: 'Outro', tags: '', related_triggers: '' });
      fetchData();
    } catch (err) { toast.error('Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir esta solução?')) return;
    await axios.delete(`${API}/kb/solutions/${id}`, { headers: getAuthHeader() });
    toast.success('Excluída'); fetchData();
  };

  const openEdit = (s) => {
    setEditItem(s);
    setForm({
      title: s.title, content: s.content, category: s.category || 'Outro',
      tags: (s.tags || []).join(', '),
      related_triggers: (s.related_triggers || []).join(', '),
    });
    setShowForm(true);
  };

  if (loading) return <Skeleton className="h-[400px] w-full" />;

  return (
    <div className="space-y-5" data-testid="kb-solutions">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Soluções Técnicas</h2>
          <p className="text-sm text-muted-foreground">Base de conhecimento com soluções para problemas recorrentes</p>
        </div>
        <Button onClick={() => { setEditItem(null); setForm({ title: '', content: '', category: 'Outro', tags: '', related_triggers: '' }); setShowForm(true); }}>
          <Plus className="h-4 w-4 mr-2" />Nova Solução
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Buscar por título, conteúdo ou tag..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      {solutions.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">
          <BookOpen className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p>Nenhuma solução cadastrada</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {solutions.map(s => (
            <Card key={s.id} className="cursor-pointer hover:shadow-sm transition-shadow" onClick={() => setExpanded(expanded === s.id ? null : s.id)}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className="text-xs">{s.category}</Badge>
                      <h3 className="font-semibold text-sm">{s.title}</h3>
                    </div>
                    {expanded !== s.id && <p className="text-xs text-muted-foreground line-clamp-1">{s.content}</p>}
                  </div>
                  <div className="flex gap-1 shrink-0 ml-3" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(s)}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(s.id)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                  </div>
                </div>
                {expanded === s.id && (
                  <div className="mt-3 pt-3 border-t space-y-2">
                    <pre className="whitespace-pre-wrap text-sm font-sans">{s.content}</pre>
                    {s.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {s.tags.map((t, i) => <Badge key={i} variant="secondary" className="text-xs"><Tag className="h-3 w-3 mr-1" />{t}</Badge>)}
                      </div>
                    )}
                    {s.related_triggers?.length > 0 && (
                      <p className="text-xs text-muted-foreground">Triggers: {s.related_triggers.join(', ')}</p>
                    )}
                    <p className="text-xs text-muted-foreground">Por {s.author} em {formatDate(s.created_at)}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={(v) => { if (!v) { setShowForm(false); setEditItem(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editItem ? 'Editar Solução' : 'Nova Solução Técnica'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Título</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex: Resolver timeout de conexão ICMP" />
            </div>
            <div className="space-y-1">
              <Label>Categoria</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Conteúdo / Solução</Label>
              <Textarea rows={8} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder="Descreva o problema, causa raiz e passos para resolução..." />
            </div>
            <div className="space-y-1">
              <Label>Tags (separadas por vírgula)</Label>
              <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="icmp, timeout, rede" />
            </div>
            <div className="space-y-1">
              <Label>Triggers Relacionadas (separadas por vírgula)</Label>
              <Input value={form.related_triggers} onChange={(e) => setForm({ ...form, related_triggers: e.target.value })} placeholder="Unavailable by ICMP ping, FS space is low" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowForm(false); setEditItem(null); }}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving}>{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{editItem ? 'Salvar' : 'Criar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
